/**
 * One cart line. Tapping the quantity or the price opens a keypad sheet; the row itself is not
 * a form, because a form on a phone means the OS keyboard.
 */

import { formatKes } from '@pos/shared';

import { Chip, Identifier, Money, cn } from '@/shared/ui';

import type { CartLine } from '../model/cartSlice.js';

export type CartLineRowProps = {
  line: CartLine;
  /** The sale's price type, so a line can say why it did or did not change on Wholesale. */
  priceType: 'retail' | 'wholesale';
  warning?: { severity: 'blocking' | 'warning'; message: string } | undefined;
  /** Priced under the lowest a salesperson may give: a manager approves it when it is charged. */
  needsApproval?: boolean;
  onEditQuantity: (line: CartLine) => void;
  onEditPrice: (line: CartLine) => void;
  onPickSerial: (line: CartLine) => void;
  onRemove: (line: CartLine) => void;
};

/**
 * What Wholesale did to this line, in words. The price only changes once a line reaches its
 * wholesale quantity, and switching to Wholesale with one case in the cart used to change nothing
 * and say nothing — a tester reported the switch as broken.
 */
function wholesaleNote(line: CartLine): { text: string; applied: boolean } | null {
  if (line.priceOverridden) return null;
  if (line.wholesaleKes === null)
    return { text: 'No wholesale price — sold at retail.', applied: false };

  const from = line.wholesaleMinQty ?? 1;
  if (line.quantity >= from) return { text: 'Wholesale price', applied: true };

  const more = from - line.quantity;
  return {
    text: `Wholesale ${formatKes(line.wholesaleKes)} from ${from} pieces — add ${more} more.`,
    applied: false,
  };
}

export function CartLineRow({
  line,
  priceType,
  warning,
  needsApproval = false,
  onEditQuantity,
  onEditPrice,
  onPickSerial,
  onRemove,
}: CartLineRowProps) {
  return (
    <div
      className={cn(
        'rounded-card bg-surface-raised p-3 shadow-sm',
        warning?.severity === 'blocking' && 'ring-1 ring-danger-border',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold leading-tight text-content-primary">
            {line.displayName}
          </p>
          <p className="mt-1 text-xs text-content-muted">
            <Identifier value={line.sku} />
          </p>

          {line.isSerialized && (
            <button
              type="button"
              onClick={() => {
                onPickSerial(line);
              }}
              className="mt-2 block"
            >
              {line.serialNo === null ? (
                <Chip tone="danger">Choose IMEI</Chip>
              ) : (
                <Chip tone="neutral" dot={false}>
                  <Identifier value={line.serialNo} />
                </Chip>
              )}
            </button>
          )}
        </div>

        <button
          type="button"
          aria-label={`Remove ${line.displayName}`}
          onClick={() => {
            onRemove(line);
          }}
          className="-mr-1 -mt-1 rounded-full p-2 text-content-muted transition-colors hover:bg-surface-sunken"
        >
          <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="m4 4 8 8m0-8-8 8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={line.isSerialized}
          onClick={() => {
            onEditQuantity(line);
          }}
          className={cn(
            'flex min-h-touch items-center gap-2 rounded-md bg-surface-sunken px-3 text-base font-semibold',
            'transition-transform duration-instant active:scale-press disabled:opacity-50',
          )}
          aria-label={`Quantity ${line.quantity}. Change.`}
        >
          {/* Labelled, not "1 ×": a lone × beside a number read as a way to remove the line. */}
          <span className="text-xs font-medium text-content-muted">Qty</span>
          <span data-nums="money">{line.quantity}</span>
        </button>

        <button
          type="button"
          onClick={() => {
            onEditPrice(line);
          }}
          className="flex min-h-touch flex-1 items-center justify-end gap-2 rounded-md px-2 transition-transform duration-instant active:scale-press"
          aria-label={`Unit price ${line.unitPriceKes} shillings. Change.`}
        >
          {line.priceOverridden && (
            <Chip tone="warning">{needsApproval ? 'Manager to approve' : 'Price changed'}</Chip>
          )}
          {/* The sum only when there is one to do: "850 ×1 = 850" said the price three times. */}
          {line.quantity > 1 && (
            <span className="text-sm text-content-muted">
              <Money kes={line.unitPriceKes} form="bare" /> each
            </span>
          )}
          <Money kes={line.unitPriceKes * line.quantity} size="lg" form="bare" />
        </button>
      </div>

      {priceType === 'wholesale' &&
        (() => {
          const note = wholesaleNote(line);
          if (note === null) return null;
          return (
            <p
              className={cn(
                'mt-2 text-xs font-medium',
                note.applied ? 'text-action-subtle-text' : 'text-content-secondary',
              )}
            >
              {note.text}
            </p>
          );
        })()}

      {warning !== undefined && (
        <p
          role={warning.severity === 'blocking' ? 'alert' : 'status'}
          className={cn(
            'mt-2 text-xs font-medium',
            warning.severity === 'blocking'
              ? 'text-danger-subtle-text'
              : 'text-warning-subtle-text',
          )}
        >
          {warning.message}
        </p>
      )}
    </div>
  );
}
